import type { ReactNode } from "react"
import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { type VariantProps, cva } from "class-variance-authority"
import { cn } from "@holigral/shared/ui/lib/utils"

// Badges are the one pill in the system — `rounded-full` here is the single
// exception to the small radii everything else takes.
//
// Names and values come from docs/design/profiling-badges-and-inputs.html,
// which draws state out of the existing tokens on two axes: border and fill
// say what state a thing is in, and a marker says whose move it is. No new
// colours, which is the point — the foundation has no amber for "waiting" and
// cannot spare Khepri for "done", Khepri being the brand's colour rather than
// a semantic one.
//
// `tag` is the older of the two: the brand foundation's plain label, tint with
// Khepri on it and no border. It is what a badge is when it is not a state.
const badgeVariants = cva(
  "group/badge inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full border border-border px-[9px] py-[5px] font-sans text-xs leading-none font-medium whitespace-nowrap text-ink transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring focus-visible:outline-solid [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    variants: {
      variant: {
        tag: "border-transparent bg-khepri-tint text-khepri-text",
        draft: "border-dashed text-muted-foreground",
        pending: "text-muted-foreground",
        progress: "bg-card",
        needs:
          "border-sun bg-sun text-sun-ink selection:bg-ink selection:text-sun",
        done: "border-transparent bg-khepri-tint text-khepri-text",
        revoked: "border-transparent text-muted-foreground line-through",
      },
    },
    defaultVariants: {
      variant: "tag",
    },
  },
)

/**
 * The second axis: whose move it is.
 *
 * `ring` is the state moving without you, `dot` the same state waiting on you,
 * and `sun` is the disc that marks a participant's own turn. `check` closes a
 * thing out. All four are drawn from the sun and the ink, so a row of badges
 * gains no colour it did not already have.
 */
const MARKERS = {
  ring: <span className="size-2 rounded-full border-[1.5px] border-sun-edge" />,
  dot: <span className="size-2 rounded-full bg-sun-ink" />,
  sun: <span className="size-2 rounded-full bg-sun" />,
  check: (
    <span aria-hidden className="text-[11px] leading-none">
      ✓
    </span>
  ),
} satisfies Record<string, ReactNode>

export type BadgeMarker = keyof typeof MARKERS

function Badge({
  className,
  variant = "tag",
  marker,
  children,
  render,
  ...props
}: useRender.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    /** Drawn before the label. See `MARKERS`. */
    readonly marker?: BadgeMarker
  }) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant }), className),
        children: (
          <>
            {marker !== undefined && MARKERS[marker]}
            {children}
          </>
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}

export { Badge, badgeVariants }
